<script setup lang="ts">
import { ref, watch, nextTick, onUnmounted, onMounted } from 'vue'
import { RouterView, RouterLink, useRoute } from 'vue-router'
import TooltipWrapper from '@/components/TooltipWrapper.vue'
import ConnectionStatus from '@/components/ConnectionStatus.vue'
import ToastContainer from '@/components/ToastContainer.vue'
import PWAInstallPrompt from '@/components/PWAInstallPrompt.vue'
import { useRealtime } from '@/composables/useRealtime'
import { useFightersStore } from '@/stores/fighters'
import { useEventsStore } from '@/stores/events'
import { initPWA } from '@/composables/usePWA'

// Initialize real-time connection
const { connect, disconnect } = useRealtime()
const fightersStore = useFightersStore()
const eventsStore = useEventsStore()

// PWA cleanup function
let cleanupPWA: (() => void) | null = null

// Connect to real-time services and subscribe stores on mount
onMounted(() => {
  connect()
  fightersStore.subscribeToRealtime()
  eventsStore.subscribeToRealtime()
  // Initialize PWA event listeners
  cleanupPWA = initPWA()
})

// Cleanup on unmount
onUnmounted(() => {
  fightersStore.unsubscribeFromRealtime()
  eventsStore.unsubscribeFromRealtime()
  disconnect()
  // Cleanup PWA listeners
  cleanupPWA?.()
})

const mobileMenuOpen = ref(false)
const route = useRoute()

// Template refs for focus management
const hamburgerButton = ref<HTMLButtonElement | null>(null)
const mobileMenuContainer = ref<HTMLElement | null>(null)

function toggleMobileMenu() {
  mobileMenuOpen.value = !mobileMenuOpen.value
}

function closeMobileMenu() {
  mobileMenuOpen.value = false
}

function closeMobileMenuAndFocus() {
  closeMobileMenu()
  nextTick(() => {
    hamburgerButton.value?.focus()
  })
}

function handleEscapeKey(event: KeyboardEvent) {
  if (event.key === 'Escape' && mobileMenuOpen.value) {
    closeMobileMenuAndFocus()
  }
}

function handleFocusTrap(event: KeyboardEvent) {
  if (!mobileMenuOpen.value || event.key !== 'Tab') return

  const focusableElements = mobileMenuContainer.value?.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
  )

  if (!focusableElements || focusableElements.length === 0) return

  const firstElement = focusableElements[0]
  const lastElement = focusableElements[focusableElements.length - 1]

  if (event.shiftKey && document.activeElement === firstElement) {
    event.preventDefault()
    lastElement.focus()
  } else if (!event.shiftKey && document.activeElement === lastElement) {
    event.preventDefault()
    firstElement.focus()
  }
}

function handleKeyDown(event: KeyboardEvent) {
  handleEscapeKey(event)
  handleFocusTrap(event)
}

// Set up global keyboard listener when menu is open
watch(mobileMenuOpen, (isOpen) => {
  if (isOpen) {
    document.addEventListener('keydown', handleKeyDown)
    nextTick(() => {
      const firstLink = mobileMenuContainer.value?.querySelector<HTMLElement>('a[href]')
      firstLink?.focus()
    })
  } else {
    document.removeEventListener('keydown', handleKeyDown)
  }
})

// Clean up listener on unmount
onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown)
})

// Close mobile menu on route change and return focus to hamburger button
watch(() => route.path, () => {
  if (mobileMenuOpen.value) {
    closeMobileMenuAndFocus()
  }
})
</script>

<template>
  <div id="app" class="min-h-screen font-sans antialiased flex flex-col">
    <a
      href="#main-content"
      class="skip-link"
    >
      Skip to main content
    </a>
    <header class="bg-ufc-dark p-4 md:p-6">
      <nav aria-label="Main navigation" class="max-w-6xl mx-auto flex items-center justify-between sm:justify-start gap-4 md:gap-6">
        <!-- Mobile hamburger button -->
        <button
          ref="hamburgerButton"
          type="button"
          class="sm:hidden min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-white hover:text-ufc-gold transition-colors focus-ring focus-ring-dark rounded"
          :aria-expanded="mobileMenuOpen"
          aria-controls="mobile-menu"
          @click="toggleMobileMenu"
        >
          <span class="sr-only">{{ mobileMenuOpen ? 'Close menu' : 'Open menu' }}</span>
          <!-- Hamburger icon -->
          <svg v-if="!mobileMenuOpen" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          <!-- Close icon -->
          <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <!-- Desktop navigation -->
        <div class="hidden sm:flex items-center gap-4 md:gap-6">
          <RouterLink v-slot="{ href, navigate, isExactActive }" to="/" custom>
            <a :href="href" :aria-current="isExactActive ? 'page' : undefined" class="min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-sm md:text-base text-white font-semibold no-underline hover:text-ufc-gold transition-colors focus-ring focus-ring-dark rounded" @click="navigate">
              Home
            </a>
          </RouterLink>
          <RouterLink v-slot="{ href, navigate, isActive }" to="/fighters" custom>
            <a :href="href" :aria-current="isActive ? 'page' : undefined" class="min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-sm md:text-base text-white font-semibold no-underline hover:text-ufc-gold transition-colors focus-ring focus-ring-dark rounded" @click="navigate">
              Fighters
            </a>
          </RouterLink>
          <RouterLink v-slot="{ href, navigate, isActive }" to="/events" custom>
            <a :href="href" :aria-current="isActive ? 'page' : undefined" class="min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-sm md:text-base text-white font-semibold no-underline hover:text-ufc-gold transition-colors focus-ring focus-ring-dark rounded" @click="navigate">
              Events
            </a>
          </RouterLink>
          <RouterLink v-slot="{ href, navigate, isActive }" to="/rankings" custom>
            <a :href="href" :aria-current="isActive ? 'page' : undefined" class="min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-sm md:text-base text-white font-semibold no-underline hover:text-ufc-gold transition-colors focus-ring focus-ring-dark rounded" @click="navigate">
              Rankings
            </a>
          </RouterLink>
          <RouterLink v-slot="{ href, navigate, isActive }" to="/news" custom>
            <a :href="href" :aria-current="isActive ? 'page' : undefined" class="min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-sm md:text-base text-white font-semibold no-underline hover:text-ufc-gold transition-colors focus-ring focus-ring-dark rounded" @click="navigate">
              News
            </a>
          </RouterLink>
          <RouterLink v-slot="{ href, navigate, isActive }" to="/compare" custom>
            <a :href="href" :aria-current="isActive ? 'page' : undefined" class="min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-sm md:text-base text-white font-semibold no-underline hover:text-ufc-gold transition-colors focus-ring focus-ring-dark rounded" @click="navigate">
              Compare
            </a>
          </RouterLink>
          <RouterLink v-slot="{ href, navigate, isActive }" to="/pickem" custom>
            <a :href="href" :aria-current="isActive ? 'page' : undefined" class="min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-sm md:text-base text-white font-semibold no-underline hover:text-ufc-gold transition-colors focus-ring focus-ring-dark rounded" @click="navigate">
              Pick'em
            </a>
          </RouterLink>
          <RouterLink v-slot="{ href, navigate, isActive }" to="/about" custom>
            <a :href="href" :aria-current="isActive ? 'page' : undefined" class="min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-sm md:text-base text-white font-semibold no-underline hover:text-ufc-gold transition-colors focus-ring focus-ring-dark rounded" @click="navigate">
              About
            </a>
          </RouterLink>
        </div>

        <!-- Logo/brand placeholder for mobile (keeps layout balanced) -->
        <span class="sm:hidden text-white font-bold text-lg">UFC</span>

        <!-- Connection status indicator (desktop) -->
        <div class="hidden sm:flex items-center ml-auto">
          <ConnectionStatus :show-label="true" size="sm" />
        </div>
      </nav>

      <!-- Mobile menu dropdown -->
      <Transition name="mobile-menu">
        <div
          v-if="mobileMenuOpen"
          ref="mobileMenuContainer"
          id="mobile-menu"
          role="navigation"
          aria-label="Mobile navigation"
          class="sm:hidden mt-4 border-t border-gray-700 pt-4 overflow-hidden"
        >
          <div class="flex flex-col gap-2">
            <!-- Close button - positioned at top of menu for clear visual association -->
            <div class="flex justify-end mb-2">
              <button
                type="button"
                class="min-h-[44px] min-w-[44px] inline-flex items-center justify-center gap-2 text-white hover:text-ufc-gold hover:bg-gray-800 transition-colors focus-ring focus-ring-dark rounded px-3"
                aria-label="Close menu"
                @click="closeMobileMenuAndFocus"
              >
                <span class="text-sm font-medium">Close</span>
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <RouterLink v-slot="{ href, navigate, isExactActive }" to="/" custom>
              <a
                :href="href"
                :aria-current="isExactActive ? 'page' : undefined"
                class="min-h-[44px] px-4 py-3 inline-flex items-center text-base text-white font-semibold no-underline hover:text-ufc-gold hover:bg-gray-800 transition-colors rounded focus-ring focus-ring-dark"
                :class="{ 'text-ufc-gold-accessible bg-gray-800': isExactActive }"
                @click="navigate"
              >
                Home
              </a>
            </RouterLink>
            <RouterLink v-slot="{ href, navigate, isActive }" to="/fighters" custom>
              <a
                :href="href"
                :aria-current="isActive ? 'page' : undefined"
                class="min-h-[44px] px-4 py-3 inline-flex items-center text-base text-white font-semibold no-underline hover:text-ufc-gold hover:bg-gray-800 transition-colors rounded focus-ring focus-ring-dark"
                :class="{ 'text-ufc-gold-accessible bg-gray-800': isActive }"
                @click="navigate"
              >
                Fighters
              </a>
            </RouterLink>
            <RouterLink v-slot="{ href, navigate, isActive }" to="/events" custom>
              <a
                :href="href"
                :aria-current="isActive ? 'page' : undefined"
                class="min-h-[44px] px-4 py-3 inline-flex items-center text-base text-white font-semibold no-underline hover:text-ufc-gold hover:bg-gray-800 transition-colors rounded focus-ring focus-ring-dark"
                :class="{ 'text-ufc-gold-accessible bg-gray-800': isActive }"
                @click="navigate"
              >
                Events
              </a>
            </RouterLink>
            <RouterLink v-slot="{ href, navigate, isActive }" to="/rankings" custom>
              <a
                :href="href"
                :aria-current="isActive ? 'page' : undefined"
                class="min-h-[44px] px-4 py-3 inline-flex items-center text-base text-white font-semibold no-underline hover:text-ufc-gold hover:bg-gray-800 transition-colors rounded focus-ring focus-ring-dark"
                :class="{ 'text-ufc-gold-accessible bg-gray-800': isActive }"
                @click="navigate"
              >
                Rankings
              </a>
            </RouterLink>
            <RouterLink v-slot="{ href, navigate, isActive }" to="/news" custom>
              <a
                :href="href"
                :aria-current="isActive ? 'page' : undefined"
                class="min-h-[44px] px-4 py-3 inline-flex items-center text-base text-white font-semibold no-underline hover:text-ufc-gold hover:bg-gray-800 transition-colors rounded focus-ring focus-ring-dark"
                :class="{ 'text-ufc-gold-accessible bg-gray-800': isActive }"
                @click="navigate"
              >
                News
              </a>
            </RouterLink>
            <RouterLink v-slot="{ href, navigate, isActive }" to="/compare" custom>
              <a
                :href="href"
                :aria-current="isActive ? 'page' : undefined"
                class="min-h-[44px] px-4 py-3 inline-flex items-center text-base text-white font-semibold no-underline hover:text-ufc-gold hover:bg-gray-800 transition-colors rounded focus-ring focus-ring-dark"
                :class="{ 'text-ufc-gold-accessible bg-gray-800': isActive }"
                @click="navigate"
              >
                Compare
              </a>
            </RouterLink>
            <RouterLink v-slot="{ href, navigate, isActive }" to="/pickem" custom>
              <a
                :href="href"
                :aria-current="isActive ? 'page' : undefined"
                class="min-h-[44px] px-4 py-3 inline-flex items-center text-base text-white font-semibold no-underline hover:text-ufc-gold hover:bg-gray-800 transition-colors rounded focus-ring focus-ring-dark"
                :class="{ 'text-ufc-gold-accessible bg-gray-800': isActive }"
                @click="navigate"
              >
                Pick'em
              </a>
            </RouterLink>
            <RouterLink v-slot="{ href, navigate, isActive }" to="/about" custom>
              <a
                :href="href"
                :aria-current="isActive ? 'page' : undefined"
                class="min-h-[44px] px-4 py-3 inline-flex items-center text-base text-white font-semibold no-underline hover:text-ufc-gold hover:bg-gray-800 transition-colors rounded focus-ring focus-ring-dark"
                :class="{ 'text-ufc-gold-accessible bg-gray-800': isActive }"
                @click="navigate"
              >
                About
              </a>
            </RouterLink>

            <!-- Connection status indicator (mobile) -->
            <div class="px-4 py-3 border-t border-gray-700 mt-2">
              <ConnectionStatus :show-label="true" size="md" />
            </div>
          </div>
        </div>
      </Transition>
    </header>
    <main id="main-content" tabindex="-1" class="flex-grow focus:outline-none">
      <RouterView />
    </main>
    <footer aria-label="Site footer" class="bg-ufc-dark border-t border-gray-700 mt-auto">
      <div class="max-w-6xl mx-auto px-4 py-8 md:py-12">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <!-- Site Links -->
          <nav aria-label="Footer navigation">
            <h2 class="text-h4 text-ufc-gold-accessible mb-4">Quick Links</h2>
            <ul class="space-y-1">
              <li>
                <RouterLink to="/" class="text-gray-300 hover:text-ufc-gold transition-colors focus-ring focus-ring-dark rounded inline-flex items-center min-h-[44px] px-2 -mx-2">
                  Home
                </RouterLink>
              </li>
              <li>
                <RouterLink to="/fighters" class="text-gray-300 hover:text-ufc-gold transition-colors focus-ring focus-ring-dark rounded inline-flex items-center min-h-[44px] px-2 -mx-2">
                  Fighters
                </RouterLink>
              </li>
              <li>
                <RouterLink to="/events" class="text-gray-300 hover:text-ufc-gold transition-colors focus-ring focus-ring-dark rounded inline-flex items-center min-h-[44px] px-2 -mx-2">
                  Events
                </RouterLink>
              </li>
              <li>
                <RouterLink to="/rankings" class="text-gray-300 hover:text-ufc-gold transition-colors focus-ring focus-ring-dark rounded inline-flex items-center min-h-[44px] px-2 -mx-2">
                  Rankings
                </RouterLink>
              </li>
              <li>
                <RouterLink to="/news" class="text-gray-300 hover:text-ufc-gold transition-colors focus-ring focus-ring-dark rounded inline-flex items-center min-h-[44px] px-2 -mx-2">
                  News
                </RouterLink>
              </li>
              <li>
                <RouterLink to="/compare" class="text-gray-300 hover:text-ufc-gold transition-colors focus-ring focus-ring-dark rounded inline-flex items-center min-h-[44px] px-2 -mx-2">
                  Compare
                </RouterLink>
              </li>
              <li>
                <RouterLink to="/pickem" class="text-gray-300 hover:text-ufc-gold transition-colors focus-ring focus-ring-dark rounded inline-flex items-center min-h-[44px] px-2 -mx-2">
                  Pick'em
                </RouterLink>
              </li>
              <li>
                <RouterLink to="/about" class="text-gray-300 hover:text-ufc-gold transition-colors focus-ring focus-ring-dark rounded inline-flex items-center min-h-[44px] px-2 -mx-2">
                  About
                </RouterLink>
              </li>
            </ul>
          </nav>

          <!-- Social Media Links -->
          <div>
            <h2 class="text-h4 text-ufc-gold-accessible mb-4">Follow Us</h2>
            <ul class="flex gap-4">
              <li>
                <TooltipWrapper text="Twitter" position="top">
                  <a
                    href="https://twitter.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-gray-300 hover:text-ufc-gold transition-colors focus-ring focus-ring-dark rounded inline-flex items-center justify-center min-h-[44px] min-w-[44px]"
                    aria-label="Follow us on Twitter"
                  >
                    <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                    </svg>
                  </a>
                </TooltipWrapper>
              </li>
              <li>
                <TooltipWrapper text="Instagram" position="top">
                  <a
                    href="https://instagram.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-gray-300 hover:text-ufc-gold transition-colors focus-ring focus-ring-dark rounded inline-flex items-center justify-center min-h-[44px] min-w-[44px]"
                    aria-label="Follow us on Instagram"
                  >
                    <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path fill-rule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clip-rule="evenodd" />
                    </svg>
                  </a>
                </TooltipWrapper>
              </li>
              <li>
                <TooltipWrapper text="YouTube" position="top">
                  <a
                    href="https://youtube.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-gray-300 hover:text-ufc-gold transition-colors focus-ring focus-ring-dark rounded inline-flex items-center justify-center min-h-[44px] min-w-[44px]"
                    aria-label="Subscribe on YouTube"
                  >
                    <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path fill-rule="evenodd" d="M19.812 5.418c.861.23 1.538.907 1.768 1.768C21.998 8.746 22 12 22 12s0 3.255-.418 4.814a2.504 2.504 0 0 1-1.768 1.768c-1.56.419-7.814.419-7.814.419s-6.255 0-7.814-.419a2.505 2.505 0 0 1-1.768-1.768C2 15.255 2 12 2 12s0-3.255.417-4.814a2.507 2.507 0 0 1 1.768-1.768C5.744 5 11.998 5 11.998 5s6.255 0 7.814.418ZM15.194 12 10 15V9l5.194 3Z" clip-rule="evenodd" />
                    </svg>
                  </a>
                </TooltipWrapper>
              </li>
            </ul>
          </div>

          <!-- Copyright & Legal -->
          <div>
            <h2 class="text-h4 text-ufc-gold-accessible mb-4">Legal</h2>
            <p class="text-gray-400 text-sm mb-2">
              &copy; {{ new Date().getFullYear() }} UFC Fan Site. All rights reserved.
            </p>
            <p class="text-gray-400 text-xs">
              This is a fan-made website and is not affiliated with UFC or Zuffa, LLC.
            </p>
          </div>
        </div>
      </div>
    </footer>
    <ToastContainer />
    <PWAInstallPrompt />
  </div>
</template>
